import { createAgencyRealtimeTicket } from '@/api/tourLeaderAgency'
import { getToken } from '@/utils/auth'

const configuredUrl = String(process.env.VUE_APP_TOUR_LEADER_WS_URL || '')
const configuredEnabled =
  String(process.env.VUE_APP_TOUR_LEADER_WS_ENABLED || '').toLowerCase() === 'true' &&
  Boolean(configuredUrl)

function defaultTicketProvider() {
  if (!getToken()) return Promise.reject(new Error('session_inactive'))
  return createAgencyRealtimeTicket()
}

function responseBody(response) {
  const httpBody = response && response.__httpResponse ? response.body : response
  return (httpBody && httpBody.data) || httpBody || {}
}

function ticketFrom(response) {
  const ticket = String(responseBody(response).ticket || '')
  if (!ticket || /[\s,]/.test(ticket)) throw new Error('realtime_ticket_invalid')
  return ticket
}

function eventIdentity(event) {
  if (!event || (!event.eventId && !event.messageId)) return ''
  return `${String(event.conversationId || '')}:${String(event.eventId || event.messageId)}`
}

function secureWebSocketUrl(value) {
  const url = new URL(String(value))
  if (
    !['ws:', 'wss:'].includes(url.protocol) ||
    url.username ||
    url.password ||
    url.search ||
    url.hash
  ) {
    throw new Error('realtime_url_invalid')
  }
  return url.toString()
}

export class TourLeaderAgencyRealtime {
  constructor(options = {}) {
    this.enabled = options.enabled === undefined ? configuredEnabled : options.enabled
    this.url = options.url || configuredUrl
    this.ticketProvider = options.ticketProvider || defaultTicketProvider
    this.createSocket = options.createSocket || ((url, protocols) => new WebSocket(url, protocols))
    this.minimumReconnectDelay = options.minimumReconnectDelay || 1000
    this.maximumReconnectDelay = options.maximumReconnectDelay || 30000
    this.listeners = new Set()
    this.seenEventIds = new Set()
    this.socket = null
    this.connectPromise = null
    this.connectPromiseGeneration = null
    this.reconnectTimer = null
    this.reconnectAttempt = 0
    this.consumers = 0
    this.stopped = true
    this.generation = 0
    this.status = 'disconnected'
  }

  subscribe(listener) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  retain() {
    this.consumers += 1
    if (this.enabled) void this.connect()
    let released = false
    return () => {
      if (released) return
      released = true
      this.consumers = Math.max(0, this.consumers - 1)
      if (this.consumers === 0) this.disconnect()
    }
  }

  async connect() {
    if (!this.enabled || !this.url) {
      this.status = 'disabled'
      this.stopped = true
      return
    }
    this.stopped = false
    if (this.socket && [0, 1].includes(this.socket.readyState)) return
    if (this.connectPromise && this.connectPromiseGeneration === this.generation) {
      return this.connectPromise
    }

    const generation = this.generation
    this.status = 'connecting'
    const pending = Promise.resolve()
      .then(() => this.ticketProvider())
      .then(response => {
        if (this.stopped || generation !== this.generation) return
        const socket = this.createSocket(secureWebSocketUrl(this.url), [
          'TourLeader.v1',
          ticketFrom(response),
        ])
        this.socket = socket
        socket.addEventListener('open', () => {
          if (this.socket !== socket) return
          this.reconnectAttempt = 0
          this.status = 'connected'
          this.emit({ type: 'socket.connected' })
        })
        socket.addEventListener('message', rawEvent => {
          if (this.socket === socket) this.handleMessage(rawEvent)
        })
        socket.addEventListener('error', () => {
          if (this.socket === socket) socket.close()
        })
        socket.addEventListener('close', () => {
          if (this.socket !== socket) return
          this.socket = null
          this.scheduleReconnect()
        })
      })
      .catch(() => {
        if (!this.stopped && generation === this.generation) this.scheduleReconnect()
      })
      .finally(() => {
        if (this.connectPromise === pending) {
          this.connectPromise = null
          this.connectPromiseGeneration = null
        }
        if (
          generation !== this.generation &&
          !this.stopped &&
          this.enabled &&
          this.consumers > 0 &&
          !this.connectPromise &&
          !(this.socket && [0, 1].includes(this.socket.readyState))
        ) {
          void this.connect()
        }
      })
    this.connectPromise = pending
    this.connectPromiseGeneration = generation
    return pending
  }

  handleMessage(rawEvent) {
    let event
    try {
      event = JSON.parse(rawEvent.data)
    } catch (error) {
      return
    }
    if (!event || typeof event !== 'object' || Array.isArray(event)) return
    const identity = eventIdentity(event)
    if (identity && this.seenEventIds.has(identity)) return
    if (identity) {
      this.seenEventIds.add(identity)
      if (this.seenEventIds.size > 500) {
        this.seenEventIds.delete(this.seenEventIds.values().next().value)
      }
    }
    this.emit(event)
  }

  emit(event) {
    this.listeners.forEach(listener => {
      try {
        listener(event)
      } catch (error) {
        // A page listener cannot terminate delivery for other consumers.
      }
    })
  }

  scheduleReconnect() {
    this.socket = null
    this.status = 'disconnected'
    if (this.stopped || !this.enabled) return
    const delay = Math.min(
      this.maximumReconnectDelay,
      this.minimumReconnectDelay * 2 ** this.reconnectAttempt
    )
    this.reconnectAttempt += 1
    clearTimeout(this.reconnectTimer)
    this.reconnectTimer = setTimeout(() => void this.connect(), delay)
  }

  disconnect() {
    this.stopped = true
    this.generation += 1
    this.status = 'disconnected'
    clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.reconnectAttempt = 0
    this.seenEventIds.clear()
    const socket = this.socket
    this.socket = null
    socket && socket.close()
  }
}

export const tourLeaderAgencyRealtime = new TourLeaderAgencyRealtime()
